import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ActionsGroup, DialogActions, DialogContent, DialogTitle, IconButton, RaisedButton } from "@evergis/ui";

import { Content } from "./styled";
import { ModalDialog } from "../DialogComponents/ModalDialog";
import { LayerListModule } from "../../../modules/LayerList/LayerList";
import { getSelectedLayers } from "../../../ducks/project/selectors";
import { setSelectedLayers } from "../../../ducks/project/project";
import { useDialogInfo } from "../../../hooks/useDialogs";
import { useProjectTempConfig } from "../../../modules/Configuration/hooks/useProjectTempConfig";
import { useProjectInfo } from "../../../ducks/project/hooks";
import { useWidgetContext } from "../../../modules/Dashboard/hooks/useWidgetContext";
import { ConfigFilterDialogOptions } from "./types";
import { WidgetType } from "../../../modules/FeatureCard/types";

export const ConfigLayer: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("projectConfig");
  const { options, closeDialog } = useDialogInfo<string[], ConfigFilterDialogOptions>();
  const selectedLayers = useSelector(getSelectedLayers);
  const [currentProject] = useProjectInfo();
  const [tempConfig] = useProjectTempConfig();
  const { page } = useWidgetContext(WidgetType.Dashboard);
  const { onPaste } = options;

  const pageLayers = useMemo(
    () => (tempConfig || currentProject).content.dashboardConfiguration?.children?.[0]?.children[page - 1]?.layers,
    [currentProject, page, tempConfig],
  );

  const onSubmit = useCallback(() => {
    onPaste(selectedLayers);
    closeDialog();
  }, [closeDialog, onPaste, selectedLayers]);

  useEffect(() => {
    if (pageLayers?.length) {
      dispatch(setSelectedLayers(pageLayers.map(({ name }) => name)));
    }
  }, []);

  return (
    <ModalDialog maxWidth={"40rem"}>
      <DialogTitle>
        <ActionsGroup>{t("addLayer")}</ActionsGroup>
        <ActionsGroup>
          <IconButton kind="close" onClick={closeDialog} />
        </ActionsGroup>
      </DialogTitle>
      <DialogContent>
        <Content>
          <LayerListModule isSelectable noMenu noControl noSearch noLegend noScroll noVisibilityClick hasGeometry />
        </Content>
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <RaisedButton primary onClick={onSubmit}>
            {t("actions.add", { ns: "common" })}
          </RaisedButton>
        </ActionsGroup>
      </DialogActions>
    </ModalDialog>
  );
};
